import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Space,
    Typography,
    Card,
    message,
    Popconfirm,
    Row,
    Col,
    Tag,
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    getJusticeNamesApi,
    createJusticeNameApi,
    updateJusticeNameApi,
    deleteJusticeNameApi,
} from "../../../../api/master.api";

const { Title } = Typography;

const JusticeName = () => {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // 🔥 Fetch Justice Names
    const fetchData = useCallback(async (page = 1, limit = 10, search = "") => {
        setLoading(true);
        try {
            const { data } = await getJusticeNamesApi(page, limit, search);
            setItems(data.data.items || []);
            setPagination({
                current: data.data.pagination.page,
                pageSize: data.data.pagination.limit,
                total: data.data.pagination.total,
            });
        } catch (error) {
            message.error("Failed to load justice names");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(1, pagination.pageSize, searchText);
    }, [fetchData, pagination.pageSize, searchText]);

    // 🔥 Create
    const handleCreate = async (values) => {
        try {
            await createJusticeNameApi(values);
            message.success("Justice name created successfully");
            setCreateOpen(false);
            createForm.resetFields();
            fetchData(1, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Create failed");
            console.error(error);
        }
    };

    // 🔥 Update
    const handleUpdate = async (values) => {
        try {
            await updateJusticeNameApi(selected.justice_id, values);
            message.success("Justice name updated successfully");
            setEditOpen(false);
            editForm.resetFields();
            fetchData(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Update failed");
            console.error(error);
        }
    };

    // 🔥 Delete
    const handleDelete = async (id) => {
        try {
            await deleteJusticeNameApi(id);
            message.success("Justice name deleted successfully");
            fetchData(pagination.current, pagination.pageSize, searchText);
        } catch (error) {
            message.error("Delete failed");
            console.error(error);
        }
    };

    // 🔥 Table Columns
    const columns = [
        {
            title: "ID",
            dataIndex: "justice_id",
            width: 80,
            key: "justice_id",
        },
        {
            title: "Justice Name",
            dataIndex: "justice_name",
            key: "justice_name",
            ellipsis: true,
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelected(record);
                            editForm.setFieldsValue(record);
                            setEditOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Delete this justice name?"
                        description="Are you sure you want to delete this?"
                        onConfirm={() => handleDelete(record.justice_id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Row justify="space-between" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4}>আদালতের নাম (Justice Name)</Title>
                    <Tag color="blue" style={{ marginTop: 5 }}>
                        Total: {pagination.total}
                    </Tag>
                </Col>
                <Col>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchData(pagination.current, pagination.pageSize, searchText)}
                        >
                            Refresh
                        </Button>
                        <Input.Search
                            placeholder="Search justice name..."
                            allowClear
                            onSearch={(value) => {
                                setSearchText(value);
                                fetchData(1, pagination.pageSize, value);
                            }}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    setSearchText("");
                                    fetchData(1, pagination.pageSize, "");
                                }
                            }}
                            enterButton={<SearchOutlined />}
                            style={{ width: 250 }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateOpen(true)}
                        >
                            Add New Justice Name
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="justice_id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        onChange: (page, pageSize) => {
                            fetchData(page, pageSize, searchText);
                        },
                        onShowSizeChange: (current, size) => {
                            fetchData(1, size, searchText);
                        },
                    }}
                    scroll={{ x: 768 }}
                />
            </Card>

            {/* CREATE MODAL */}
            <Modal
                title="Add New Justice Name"
                open={createOpen}
                onCancel={() => {
                    setCreateOpen(false);
                    createForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={createForm}
                    onFinish={handleCreate}
                    layout="vertical"
                    initialValues={{ justice_name: "" }}
                >
                    <Form.Item
                        name="justice_name"
                        label="Justice Name"
                        rules={[
                            { required: true, message: "Please enter justice name" },
                            { min: 2, message: "Minimum 2 characters required" },
                        ]}
                    >
                        <Input
                            placeholder="Enter justice name"
                            size="large"
                            autoFocus
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setCreateOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Create
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* EDIT MODAL */}
            <Modal
                title="Edit Justice Name"
                open={editOpen}
                onCancel={() => {
                    setEditOpen(false);
                    editForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={editForm}
                    onFinish={handleUpdate}
                    layout="vertical"
                >
                    <Form.Item
                        name="justice_name"
                        label="Justice Name"
                        rules={[
                            { required: true, message: "Please enter justice name" },
                            { min: 2, message: "Minimum 2 characters required" },
                        ]}
                    >
                        <Input
                            placeholder="Enter justice name"
                            size="large"
                            autoFocus
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setEditOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Update
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default JusticeName;